import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  FaCloudUploadAlt,
  FaGem,
  FaRegCalendarAlt,
  FaRegHeart,
  FaRegStar,
  FaStar,
  FaUserAlt,
} from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { API_BASE } from "../config/api";
import {
  bindStorefrontRefresh,
  STOREFRONT_REFRESH_SCOPES,
} from "../utils/storefrontRefresh";
import "./BespokeJewelryPage.css";

const ICON_MAP = {
  user: FaUserAlt,
  heart: FaRegHeart,
  gem: FaGem,
  calendar: FaRegCalendarAlt,
  star: FaRegStar,
};

const LEGACY_DEFAULT_FILE_ACCEPT = ".jpg,.jpeg,.png,.webp,.pdf";
const DEFAULT_FILE_ACCEPT = ".jpg,.jpeg,.jfif,.png,.webp,.avif,.heic,.heif,.pdf";

const FILE_TYPE_ALIASES = {
  ".jpg": ["image/jpeg", "image/jpg", "image/pjpeg"],
  ".jpeg": ["image/jpeg", "image/jpg", "image/pjpeg"],
  ".jfif": ["image/jpeg", "image/jpg", "image/pjpeg"],
  ".png": ["image/png"],
  ".webp": ["image/webp"],
  ".avif": ["image/avif"],
  ".heic": ["image/heic", "image/heif"],
  ".heif": ["image/heif", "image/heic"],
  ".pdf": ["application/pdf", "application/x-pdf", "application/octet-stream"],
};

const getTodayForDateInput = () => {
  const now = new Date();
  const localDate = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localDate.toISOString().split("T")[0];
};

const normalizeText = (value) => String(value || "").trim();

const normalizeMimeType = (value) => {
  const normalizedValue = normalizeText(value).toLowerCase();

  if (normalizedValue === "image/jpg" || normalizedValue === "image/pjpeg") {
    return "image/jpeg";
  }

  return normalizedValue;
};

const normalizeUploadAcceptValue = (value) => {
  const normalizedValue = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

  if (!normalizedValue || normalizedValue === LEGACY_DEFAULT_FILE_ACCEPT) {
    return DEFAULT_FILE_ACCEPT;
  }

  return normalizedValue;
};

const getAcceptedFileRules = (acceptValue) => {
  const rawValues = normalizeUploadAcceptValue(acceptValue)
    .split(",")
    .map((value) => normalizeText(value).toLowerCase())
    .filter(Boolean);

  const acceptedExtensions = new Set();
  const acceptedMimeTypes = new Set();

  rawValues.forEach((value) => {
    if (value.startsWith(".")) {
      acceptedExtensions.add(value);

      (FILE_TYPE_ALIASES[value] || []).forEach((mimeType) => {
        acceptedMimeTypes.add(normalizeMimeType(mimeType));
      });

      return;
    }

    acceptedMimeTypes.add(normalizeMimeType(value));
  });

  return {
    acceptedExtensions: Array.from(acceptedExtensions),
    acceptedMimeTypes: Array.from(acceptedMimeTypes),
  };
};

const isAcceptedFile = (file, acceptValue) => {
  const fileName = normalizeText(file?.name).toLowerCase();
  const fileMimeType = normalizeMimeType(file?.type);
  const fileExtension = fileName.includes(".")
    ? fileName.slice(fileName.lastIndexOf("."))
    : "";
  const { acceptedExtensions, acceptedMimeTypes } = getAcceptedFileRules(acceptValue);

  const matchesExtension =
    !!fileExtension && acceptedExtensions.includes(fileExtension);
  const matchesMimeType =
    !!fileMimeType && acceptedMimeTypes.includes(fileMimeType);
  const inferredMimeTypeMatches =
    !!fileExtension &&
    acceptedMimeTypes.some((mimeType) =>
      (FILE_TYPE_ALIASES[fileExtension] || []).includes(mimeType)
    );

  return matchesExtension || matchesMimeType || inferredMimeTypeMatches;
};

const getUploadAcceptValue = (acceptValue) => {
  const { acceptedExtensions, acceptedMimeTypes } = getAcceptedFileRules(acceptValue);
  return [...acceptedExtensions, ...acceptedMimeTypes].join(",");
};

const formatFileSize = (bytes) => {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const isFilledValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return normalizeText(value).length > 0;
};

const matchesVisibleWhen = (visibleWhen, values) => {
  if (!visibleWhen?.field) {
    return true;
  }

  const currentValue = values?.[visibleWhen.field];

  if (visibleWhen.operator === "in") {
    return Array.isArray(visibleWhen.value) && visibleWhen.value.includes(currentValue);
  }

  if (visibleWhen.operator === "notEquals") {
    return currentValue !== visibleWhen.value;
  }

  return currentValue === visibleWhen.value;
};

const buildInitialFormValues = (sections, previousValues = {}) =>
  sections.reduce((accumulator, section) => {
    (Array.isArray(section.fields) ? section.fields : []).forEach((field) => {
      if (field.type === "file") {
        return;
      }

      accumulator[field.name] = previousValues[field.name] ?? "";
    });

    return accumulator;
  }, {});

const buildInitialFileValues = (sections, previousValues = {}) =>
  sections.reduce((accumulator, section) => {
    (Array.isArray(section.fields) ? section.fields : []).forEach((field) => {
      if (field.type !== "file") {
        return;
      }

      accumulator[field.name] = Array.isArray(previousValues[field.name])
        ? previousValues[field.name]
        : [];
    });

    return accumulator;
  }, {});

const getVisibleFields = (section, values) =>
  (Array.isArray(section.fields) ? section.fields : []).filter((field) =>
    matchesVisibleWhen(field.visibleWhen, values)
  );

const BespokeJewelryPage = () => {
  const [sections, setSections] = useState([]);
  const [jewelryTypes, setJewelryTypes] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [fileValues, setFileValues] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [error, setError] = useState("");
  const [activeDropField, setActiveDropField] = useState("");
  const [openSelectField, setOpenSelectField] = useState("");
  const [openSelectStyle, setOpenSelectStyle] = useState(null);

  useEffect(() => {
    let isActive = true;

    const loadBespokeForm = async () => {
      try {
        setLoadingConfig(true);

        const [configResponse, dropdownResponse] = await Promise.all([
          axios.get(`${API_BASE}/bespoke/config`),
          axios.get(`${API_BASE}/navbar-dropdown`),
        ]);

        if (!isActive) {
          return;
        }

        const nextSections = Array.isArray(configResponse.data.sections)
          ? configResponse.data.sections
          : [];
        const nextJewelryTypes = Array.isArray(dropdownResponse.data.jewelryTypes)
          ? dropdownResponse.data.jewelryTypes
          : [];

        setSections(nextSections);
        setJewelryTypes(nextJewelryTypes);
        setFormValues((previousValues) =>
          buildInitialFormValues(nextSections, previousValues)
        );
        setFileValues((previousFiles) =>
          buildInitialFileValues(nextSections, previousFiles)
        );
      } catch (requestError) {
        if (!isActive) {
          return;
        }

        console.error("Failed to fetch bespoke form configuration:", requestError);
        setError("Failed to load the bespoke form. Please refresh and try again.");
      } finally {
        if (isActive) {
          setLoadingConfig(false);
        }
      }
    };

    loadBespokeForm();

    const unbindRefresh = bindStorefrontRefresh(loadBespokeForm, {
      scopes: [STOREFRONT_REFRESH_SCOPES.BESPOKE, STOREFRONT_REFRESH_SCOPES.ALL],
      minIntervalMs: 5000,
    });

    return () => {
      isActive = false;
      unbindRefresh();
    };
  }, []);

  useEffect(() => {
    if (!openSelectField) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (
        event.target.closest("[data-bespoke-select-root='true']") ||
        event.target.closest("[data-bespoke-select-portal='true']")
      ) {
        return;
      }

      setOpenSelectField("");
      setOpenSelectStyle(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpenSelectField("");
        setOpenSelectStyle(null);
      }
    };

    const handleViewportChange = () => {
      setOpenSelectField("");
      setOpenSelectStyle(null);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [openSelectField]);

  const requiredFieldState = useMemo(() => {
    return sections.map((section) => {
      const visibleFields = getVisibleFields(section, formValues);

      return visibleFields.every((field) => {
        if (!field.required) {
          return true;
        }

        return field.type === "file"
          ? isFilledValue(fileValues[field.name] || [])
          : isFilledValue(formValues[field.name]);
      });
    });
  }, [fileValues, formValues, sections]);

  const isSectionLocked = useCallback((sectionIndex) =>
    sections[sectionIndex]?.lockMessageUntilComplete &&
    !requiredFieldState.slice(0, sectionIndex).every(Boolean), [requiredFieldState, sections]);

  const isFormComplete = useMemo(() => {
    return sections.every((section, sectionIndex) => {
      const visibleFields = getVisibleFields(section, formValues);

      return visibleFields.every((field) => {
        if (!field.required) {
          return true;
        }

        if (section.lockMessageUntilComplete && isSectionLocked(sectionIndex)) {
          return false;
        }

        return field.type === "file"
          ? isFilledValue(fileValues[field.name] || [])
          : isFilledValue(formValues[field.name]);
      });
    });
  }, [fileValues, formValues, sections, isSectionLocked]);

  const resolveFieldOptions = (field) => {
    if (field.source === "navbar_jewelry_types") {
      return jewelryTypes.map((type) => ({
        id: type,
        label: type,
        value: type,
      }));
    }

    return Array.isArray(field.options) ? field.options : [];
  };

  const handleFieldChange = (fieldName, value) => {
    setFormValues((previousValues) => ({
      ...previousValues,
      [fieldName]: value,
    }));
    setError("");
  };

  const closeSelectDropdown = () => {
    setOpenSelectField("");
    setOpenSelectStyle(null);
  };

  const getSelectPanelWidth = (triggerWidth, optionCount) => {
    const minimumWidth =
      optionCount >= 12 ? 560 : optionCount >= 7 ? 440 : 320;

    return Math.min(
      Math.max(triggerWidth, minimumWidth),
      Math.max(window.innerWidth - 24, triggerWidth)
    );
  };

  const toggleSelectDropdown = (fieldName, optionCount, event) => {
    if (openSelectField === fieldName) {
      closeSelectDropdown();
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const width = getSelectPanelWidth(rect.width, optionCount);
    const left = Math.min(
      Math.max(12, rect.left),
      Math.max(12, window.innerWidth - width - 12)
    );

    setOpenSelectStyle({
      top: rect.bottom + 8,
      left,
      width,
    });
    setOpenSelectField(fieldName);
  };

  const handleFileSelection = (field, incomingFiles) => {
    const nextFiles = Array.from(incomingFiles || []);

    if (!nextFiles.length) {
      return;
    }

    const uploadAcceptValue = getUploadAcceptValue(field.accept);
    const { acceptedExtensions } = getAcceptedFileRules(field.accept);

    const maxFileSizeBytes =
      (Number(field.maxFileSizeMb) > 0 ? Number(field.maxFileSizeMb) : 10) * 1024 * 1024;
    const maxFiles = Math.max(Number(field.maxFiles || 5), 1);

    const invalidFile = nextFiles.find((file) => !isAcceptedFile(file, uploadAcceptValue));

    if (invalidFile) {
      setError(
        `Only ${acceptedExtensions.join(", ")} files are allowed for ${field.label}.`
      );
      return;
    }

    const oversizedFile = nextFiles.find((file) => file.size > maxFileSizeBytes);

    if (oversizedFile) {
      setError(
        `${field.label} files must be ${Number(field.maxFileSizeMb || 10)}MB or smaller.`
      );
      return;
    }

    setFileValues((previousValues) => {
      const currentFiles = Array.isArray(previousValues[field.name])
        ? previousValues[field.name]
        : [];
      const mergedFiles = [...currentFiles];

      nextFiles.forEach((file) => {
        const alreadyExists = mergedFiles.some(
          (existingFile) =>
            existingFile.name === file.name &&
            existingFile.size === file.size &&
            existingFile.lastModified === file.lastModified
        );

        if (!alreadyExists) {
          mergedFiles.push(file);
        }
      });

      if (mergedFiles.length > maxFiles) {
        setError(`${field.label} allows up to ${maxFiles} files.`);
        return previousValues;
      }

      setError("");
      return {
        ...previousValues,
        [field.name]: mergedFiles,
      };
    });
  };

  const removeFile = (fieldName, fileToRemove) => {
    setFileValues((previousValues) => ({
      ...previousValues,
      [fieldName]: (previousValues[fieldName] || []).filter((file) => file !== fileToRemove),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!isFormComplete) {
      setError("Please complete all required fields before submitting your request.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const payload = new FormData();
      payload.append("formData", JSON.stringify(formValues));

      Object.entries(fileValues).forEach(([fieldName, files]) => {
        (files || []).forEach((file) => {
          payload.append(fieldName, file);
        });
      });

      const response = await axios.post(`${API_BASE}/bespoke/submit`, payload);

      if (response.data.success) {
        setSubmitted(true);
        setFormValues(buildInitialFormValues(sections));
        setFileValues(buildInitialFileValues(sections));
      }
    } catch (submissionError) {
      setError(
        submissionError.response?.data?.message ||
          "Failed to submit your request. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const renderField = (field, section) => {
    const options = resolveFieldOptions(field);
    const fieldFiles = fileValues[field.name] || [];
    const fieldValue = formValues[field.name] ?? "";
    const isLockedSection = isSectionLocked(sections.findIndex((item) => item.id === section.id));

    if (field.type === "file") {
      const uploadInputId = `bespoke-upload-${field.id}`;
      const uploadAcceptValue = getUploadAcceptValue(field.accept);

      return (
        <div
          key={field.id}
          className="bespoke-field bespoke-field--full"
          style={{ gridColumn: `span ${Math.min(field.span || section.columns, section.columns)}` }}
        >
          <div className="bespoke-upload-row">
            <div className="bespoke-upload-copy">
              <span className="bespoke-upload-copy__icon">
                <FaCloudUploadAlt />
              </span>
              <div>
                <h3>{field.label}</h3>
                <p>{field.helperText || "Upload supporting files for this field."}</p>
              </div>
            </div>

            <input
              id={uploadInputId}
              className="bespoke-visually-hidden bespoke-upload-zone__input"
              type="file"
              accept={uploadAcceptValue}
              multiple={Number(field.maxFiles || 1) > 1}
              onChange={(changeEvent) => {
                handleFileSelection(field, changeEvent.target.files);
                changeEvent.target.value = "";
              }}
            />

            <label
              htmlFor={uploadInputId}
              className={`bespoke-upload-zone${
                activeDropField === field.name ? " is-active" : ""
              }`}
              onDragOver={(dragEvent) => {
                dragEvent.preventDefault();
                setActiveDropField(field.name);
              }}
              onDragLeave={(dragEvent) => {
                dragEvent.preventDefault();
                setActiveDropField("");
              }}
              onDrop={(dropEvent) => {
                dropEvent.preventDefault();
                setActiveDropField("");
                handleFileSelection(field, dropEvent.dataTransfer.files);
              }}
            >
              <FaCloudUploadAlt />
              <strong>Click to upload or drag and drop</strong>
              <span>
                {normalizeUploadAcceptValue(field.accept || DEFAULT_FILE_ACCEPT)
                  .replaceAll(",", ", ")
                  .toUpperCase()}{" "}
                (Max. {field.maxFileSizeMb || 10}MB each)
              </span>
            </label>
          </div>

          {fieldFiles.length ? (
            <div className="bespoke-file-list">
              {fieldFiles.map((file) => (
                <button
                  key={`${file.name}-${file.lastModified}`}
                  type="button"
                  className="bespoke-file-pill"
                  onClick={() => removeFile(field.name, file)}
                >
                  <span>{file.name}</span>
                  <small>{formatFileSize(file.size)}</small>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      );
    }

    if (field.type === "textarea") {
      return (
        <label
          key={field.id}
          className="bespoke-field bespoke-field--full"
          style={{ gridColumn: `span ${Math.min(field.span || section.columns, section.columns)}` }}
        >
          <span>{field.label}</span>
          <textarea
            name={field.name}
            value={fieldValue}
            onChange={(changeEvent) => handleFieldChange(field.name, changeEvent.target.value)}
            placeholder={
              isLockedSection
                ? "Complete the sections above to unlock this section."
                : field.placeholder || ""
            }
            disabled={isLockedSection}
            rows={6}
            maxLength={field.maxLength || undefined}
            required={field.required}
          />
          <div className="bespoke-message-footer">
            <span>
              {isLockedSection
                ? "The structured brief above needs to be completed before you can add extra notes."
                : field.helperText ||
                  "Tell us anything that would help us shape the final piece."}
            </span>
            {field.maxLength ? (
              <strong>
                {String(fieldValue).length} / {field.maxLength}
              </strong>
            ) : null}
          </div>
        </label>
      );
    }

    if (field.type === "radio") {
      return (
        <div
          key={field.id}
          className="bespoke-field"
          style={{ gridColumn: `span ${Math.min(field.span || 1, section.columns)}` }}
        >
          <span>{field.label}</span>
          <div className="bespoke-sizing-row">
            {options.map((option) => (
              <label key={option.id} className="bespoke-radio">
                <input
                  type="radio"
                  name={field.name}
                  value={option.value}
                  checked={fieldValue === option.value}
                  onChange={(changeEvent) =>
                    handleFieldChange(field.name, changeEvent.target.value)
                  }
                />
                <span>{option.label}</span>
              </label>
            ))}
          </div>
        </div>
      );
    }

    if (field.type === "select") {
      const selectedOption = options.find((option) => option.value === fieldValue);
      const isSelectOpen = openSelectField === field.name;
      const optionColumnCount = options.length >= 12 ? 2 : 1;
      const triggerLabel = selectedOption?.label || field.placeholder || `Select ${field.label}`;

      return (
        <div
          key={field.id}
          className="bespoke-field bespoke-field--select"
          data-bespoke-select-root="true"
          style={{ gridColumn: `span ${Math.min(field.span || 1, section.columns)}` }}
        >
          <span>{field.label}</span>
          <button
            type="button"
            className={`bespoke-select__trigger${isSelectOpen ? " is-open" : ""}`}
            aria-haspopup="listbox"
            aria-expanded={isSelectOpen}
            onClick={(clickEvent) =>
              toggleSelectDropdown(field.name, options.length, clickEvent)
            }
          >
            <span
              className={`bespoke-select__value${
                selectedOption ? "" : " is-placeholder"
              }`}
            >
              {triggerLabel}
            </span>
            <IoIosArrowDown />
          </button>

          {isSelectOpen && openSelectStyle
            ? createPortal(
                <div
                  className="bespoke-select__panel"
                  style={openSelectStyle}
                  data-bespoke-select-portal="true"
                >
                  <div className="bespoke-select__heading">{field.label}</div>
                  <div
                    className={`bespoke-select__options${
                      optionColumnCount > 1 ? " is-multi-column" : ""
                    }`}
                    style={{
                      gridTemplateColumns: `repeat(${optionColumnCount}, minmax(0, 1fr))`,
                    }}
                    role="listbox"
                    aria-label={field.label}
                  >
                    {options.map((option) => (
                      <button
                        key={option.id}
                        type="button"
                        className={`bespoke-select__option${
                          fieldValue === option.value ? " is-selected" : ""
                        }`}
                        onClick={() => {
                          handleFieldChange(field.name, option.value);
                          closeSelectDropdown();
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>,
                document.body
              )
            : null}
        </div>
      );
    }

    return (
      <label
        key={field.id}
        className="bespoke-field"
        style={{ gridColumn: `span ${Math.min(field.span || 1, section.columns)}` }}
      >
        <span>{field.label}</span>
        <input
          type={field.type === "date" ? "date" : field.type}
          name={field.name}
          value={fieldValue}
          onChange={(changeEvent) => handleFieldChange(field.name, changeEvent.target.value)}
          placeholder={field.placeholder || ""}
          min={field.type === "date" ? getTodayForDateInput() : undefined}
          required={field.required}
        />
      </label>
    );
  };

  return (
    <div className="bespoke-page">
      <section className="bespoke-hero" aria-labelledby="bespoke-page-title">
        <h1 id="bespoke-page-title" className="bespoke-hero__title">
          Let&apos;s Design Something Timeless
        </h1>
      </section>

      {submitted ? (
        <section className="bespoke-success-card">
          <h2>Thank you for sharing your vision with us!</h2>
          <p>
            We have successfully received your inquiry for a bespoke piece. Our lead
            designer is currently reviewing your details and inspiration.
          </p>
          <p className="bespoke-success-card__label">What&apos;s next?</p>
          <p>
            You will receive a copy of your inquiry via email shortly. One of our
            specialists will reach out to you within 24-48 hours to discuss the
            next steps of your custom creation.
          </p>
          <p>We look forward to crafting something beautiful together.</p>
        </section>
      ) : (
        <form className="bespoke-form-shell" onSubmit={handleSubmit}>
          {error ? <div className="bespoke-form-error">{error}</div> : null}

          {loadingConfig ? (
            <section className="bespoke-success-card">
              <h2>Loading bespoke form...</h2>
              <p>We&apos;re preparing the latest custom jewelry brief for you.</p>
            </section>
          ) : (
            <>
              {sections.map((section, sectionIndex) => {
                const SectionIcon = ICON_MAP[section.iconKey] || FaRegStar;

                return (
                  <section
                    key={section.id}
                    className={`bespoke-card${
                      section.lockMessageUntilComplete && isSectionLocked(sectionIndex)
                        ? " bespoke-card--message is-locked"
                        : ""
                    }`}
                  >
                    <div className="bespoke-card__header">
                      <div
                        className={`bespoke-card__icon bespoke-card__icon--${
                          section.iconTone || "blush"
                        }`}
                      >
                        <SectionIcon />
                      </div>
                      <div>
                        <h2>{section.title}</h2>
                        <p>{section.description}</p>
                      </div>
                    </div>

                    <div
                      className="bespoke-grid"
                      style={{
                        gridTemplateColumns: `repeat(${Math.max(
                          Number(section.columns) || 1,
                          1
                        )}, minmax(0, 1fr))`,
                      }}
                    >
                      {getVisibleFields(section, formValues).map((field) =>
                        renderField(field, section)
                      )}
                    </div>
                  </section>
                );
              })}

              <div className="bespoke-submit-wrap">
                <button
                  type="submit"
                  className="bespoke-submit-button"
                  disabled={loading || !isFormComplete}
                >
                  <FaStar />
                  <span>{loading ? "Submitting..." : "Submit My Request"}</span>
                </button>
                <p>We typically respond within 24-48 hours.</p>
              </div>

              <section className="bespoke-support-note" aria-label="Bespoke support contact">
                <h3>Need Help?</h3>
                <p>
                  For any query, contact us at{" "}
                  <a href="tel:+919152995955">+91 9152995955</a>
                </p>
              </section>
            </>
          )}
        </form>
      )}
    </div>
  );
};

export default BespokeJewelryPage;
